import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { AlertTriangle, Crown, X } from "lucide-react";

/**
 * OrgSwitchBanner — Master-only banner visible while impersonating another org.
 * Shows the current impersonated org name + a "Return to Master" button.
 */
export default function OrgSwitchBanner() {
  const [status, setStatus] = useState(null);
  const [loaded, setLoaded] = useState(false);

  const refresh = () => {
    api.get("/master/switch-org")
      .then((r) => setStatus(r.data))
      .catch(() => setStatus(null))
      .finally(() => setLoaded(true));
  };
  useEffect(() => {
    refresh();
    // Refresh when nav happens (route change), by listening to popstate + a custom event
    const onSwitch = () => refresh();
    window.addEventListener("hubcmd:org-switched", onSwitch);
    return () => window.removeEventListener("hubcmd:org-switched", onSwitch);
  }, []);

  const returnToMaster = async () => {
    try {
      await api.post("/master/switch-org", { org_id: null });
      toast.success("Returned to Master mode.");
      window.dispatchEvent(new Event("hubcmd:org-switched"));
      window.location.reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to return");
    }
  };

  if (!loaded || !status?.impersonating) return null;

  return (
    <div className="bg-[#ff4500] text-white px-6 py-2 flex items-center justify-between gap-4 shadow-lg"
      data-testid="org-switch-banner">
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center animate-pulse">
          <Crown className="w-4 h-4" />
        </div>
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] font-bold">
            <AlertTriangle className="w-3 h-3 inline mr-1" /> Master · impersonation active
          </div>
          <div className="font-display font-bold text-sm">
            You are viewing hubCMD as <span className="underline">{status.org?.name}</span>
          </div>
        </div>
      </div>
      <button data-testid="banner-return-master" onClick={returnToMaster}
        className="btn-pill bg-white text-[#ff4500] hover:bg-[#fff1ea] text-[11px] font-bold">
        <X className="w-3 h-3" /> Return to Master
      </button>
    </div>
  );
}
